import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, Rating, Counter } from '../../shared/components/Common';
import { SmartImg } from '../../shared/components/Layout';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { addDaysIso, formatPrice, todayIso } from '../../shared/data/common';
import { CHILD_FACTOR, getActivity } from './data';

export default function SightseeingDetails() {
  const { activityId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const q = useQuery();
  const act = getActivity(activityId);
  const [date, setDate] = useState(q.date || addDaysIso(3));
  const [slot, setSlot] = useState(act ? act.slots[0] : '');
  const [optKey, setOptKey] = useState('std');
  const [adults, setAdults] = useState(Number(q.adults || 2));
  const [children, setChildren] = useState(Number(q.children || 0));

  if (!act) return <div className="container section empty"><h2>Activity not found</h2><Link to="/sightseeing" className="btn btn-primary">Browse activities</Link></div>;

  const opt = act.options.find((o) => o.key === optKey);
  const childPrice = Math.round(opt.price * CHILD_FACTOR);
  const total = opt.price * adults + childPrice * children;

  const book = () => navigate('/sightseeing/review', {
    state: {
      summary: {
        activityId: act.id, title: act.title, image: act.image, city: act.city, date, slot, optionName: opt.name,
        adults, children, duration: act.duration, meeting: act.meeting, adultPrice: opt.price, childPrice,
      },
      backTo: `/sightseeing/${act.id}${search}`,
    },
  });

  return (
    <>
      <FlowBand title={act.title} step={1} back={{ to: `/sightseeing/results${search}`, label: 'Back to activities' }} />
      <div className="container">
        <div className="pkg-hero">
          <SmartImg src={act.image} alt={act.title} />
          <div className="pkg-hero-info">
            <span className="tag">{act.category}</span>
            <h2>{act.title}</h2>
            <span><Icon name="pin" size={14} /> {act.city} · <Icon name="clock" size={14} /> {act.duration}</span>
            <Rating value={act.rating} reviews={act.reviews} />
          </div>
        </div>

        <div className="details-layout">
          <div className="details-main">
            <section className="panel">
              <h3>Overview</h3>
              <p className="details-desc">{act.description}</p>
              <h4>Highlights</h4>
              <ul className="policy-list check-list">{act.highlights.map((h) => <li key={h}>{h}</li>)}</ul>
            </section>

            <section className="panel">
              <h3>Select an option</h3>
              <div className="option-list">
                {act.options.map((o) => (
                  <label key={o.key} className={`option-row ${optKey === o.key ? 'active' : ''}`}>
                    <input type="radio" name="opt" checked={optKey === o.key} onChange={() => setOptKey(o.key)} />
                    <div><b>{o.name}</b><span className="muted small">{o.desc}</span></div>
                    <b className="price">{formatPrice(o.price)}</b>
                  </label>
                ))}
              </div>
            </section>

            <section className="panel">
              <div className="incl-grid">
                <div><h4 className="text-primary">What&apos;s included</h4><ul className="policy-list check-list">{act.includes.map((i) => <li key={i}>{i}</li>)}</ul></div>
                <div><h4 className="text-coral">Not included</h4><ul className="policy-list cross-list">{act.excludes.map((i) => <li key={i}>{i}</li>)}</ul></div>
              </div>
            </section>

            <section className="panel">
              <h3>Meeting point & policies</h3>
              <ul className="policy-list">
                <li><b>Meeting point:</b> {act.meeting}</li>
                <li>Free cancellation up to 24 hours before the activity starts.</li>
                <li>Children aged 3–12 get 25% off; under 3 enter free.</li>
                <li>Show your mobile voucher at the venue along with a valid photo ID.</li>
              </ul>
            </section>
          </div>

          <aside className="details-side">
            <div className="panel price-card">
              <span className="muted small">{opt.name}</span>
              <b className="price big">{formatPrice(opt.price)}</b>
              <span className="muted small">per adult · child {formatPrice(childPrice)}</span>
              <label className="field"><span className="field-label">Date</span>
                <input type="date" value={date} min={todayIso()} onChange={(e) => setDate(e.target.value)} />
              </label>
              <div className="field"><span className="field-label">Time slot</span>
                <div className="slot-grid">{act.slots.map((s) => <button key={s} type="button" className={`slot ${slot === s ? 'active' : ''}`} onClick={() => setSlot(s)}>{s}</button>)}</div>
              </div>
              <Counter label="Adults" hint="13 yrs+" value={adults} min={1} max={15} onChange={setAdults} />
              <Counter label="Children" hint="3–12 yrs" value={children} min={0} max={10} onChange={setChildren} />
              <div className="summary-line total"><span>Total</span><b>{formatPrice(total)}</b></div>
              <button className="btn btn-primary block lg" onClick={book}>Book Now</button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
